import React, { useEffect, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAppDispatch, useAppSelector } from '../../../../common/hooks';
import { updateGlobalLoaderState } from '../../../../features/global/globalSlice';
import { useTranslation } from 'react-i18next';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1440px',
    maxHeight: '90%',
    overflow: 'scroll',
    backgroundColor: 'common.white',
    boxShadow: 24,
    color: 'common.black',
    opacity: '0'
};

const PDFWrapper = styled('div')(() => ({
    margin: 0,
    minWidth: '1440px'
}));

const InnerWrapperNewCover = styled('div')(() => ({}));

interface PDFViewerProps {
    open?: boolean;
    handleClose?: () => void;
    children?: React.ReactNode;
    filename?: string;
    download?: boolean;
    setPdfGenerateToast?: any;
}

const PDFViewer = ({ open, handleClose, children, filename, download, setPdfGenerateToast }: PDFViewerProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { globalConfig } = useAppSelector((state) => state.global);
    const { t } = useTranslation();
    const generatePDF = useCallback(async () => {
        let time = new Date().getTime();
        console.log('PDF generation started');
        try {
            dispatch(updateGlobalLoaderState(true));
            const input = document.querySelectorAll('.all-pdf') as NodeListOf<HTMLElement>;
            const len = input.length;

            const pageWidth = 291;
            const pageHeight = 210;
            const frontPageOverflowWidth = 11;

            const doc = new jsPDF({
                orientation: 'l',
                unit: 'mm',
                format: 'a4'
            });

            const addWatermark = (doc: jsPDF, tenantType: string, isFirstPage?: boolean) => {
                doc.setTextColor(theme.palette.orange[200]);
                doc.setFontSize(13);
                doc.setFont('helvetica', 'bold');
                try {
                    // @ts-ignore - Bypass TypeScript checking for this specific call
                    const gState = new doc.GState({ opacity: 0.7 });
                    // @ts-ignore
                    doc.setGState(gState);
                } catch (error) {
                    console.warn('Could not set transparency with GState', error);
                }

                const xPosition = 8;
                const yPosition = pageHeight - (isFirstPage ? 36 : 33);

                doc.saveGraphicsState();
                const trnslatedText = t('TRAIL_AND_DEMO_TEXT', { watermarkText: tenantType }).toUpperCase();
                doc.text(t(trnslatedText), xPosition, yPosition, {
                    align: 'left',
                    maxWidth: pageWidth
                });

                doc.restoreGraphicsState();
            };

            doc.setDocumentProperties({
                title: filename
            });

            for (let i = 0; i < len; i += 1) {
                const element = input[i];

                const canvas = await html2canvas(element, {
                    logging: true,
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    width: element.offsetWidth,
                    height: element.offsetHeight,
                    windowWidth: element.offsetWidth,
                    windowHeight: element.offsetHeight
                });

                const imgWidth = pageWidth;
                const imgHeight = (canvas.height * pageWidth) / canvas.width;

                let heightLeft = imgHeight;
                let position = 0;

                // for front page
                doc.addImage(
                    canvas.toDataURL('image/jpeg', 1.0),
                    'JPEG',
                    0,
                    position,
                    pageWidth + frontPageOverflowWidth,
                    imgHeight,
                    '',
                    'FAST'
                );
                if (globalConfig.tenantType === 'trial' || globalConfig.tenantType === 'demo') {
                    addWatermark(doc, globalConfig.tenantType, true);
                }

                heightLeft -= pageHeight;
                position = 1;

                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight;
                    doc.addPage();

                    // for all other pages
                    doc.addImage(
                        canvas.toDataURL('image/jpeg', 1.0),
                        'JPEG',
                        3,
                        position,
                        imgWidth,
                        imgHeight,
                        '',
                        'FAST'
                    );

                    if (globalConfig.tenantType === 'trial' || globalConfig.tenantType === 'demo') {
                        addWatermark(doc, globalConfig.tenantType);
                    }
                    heightLeft -= pageHeight;
                }

                if (i + 1 < len) {
                    doc.addPage();
                }
            }
            if (download) {
                doc.save(filename || 'plan-details.pdf');
            }
            handleClose && handleClose();
            setPdfGenerateToast && setPdfGenerateToast({ open: false, generating: false });
            dispatch(updateGlobalLoaderState(false));
        } catch (error) {
            setPdfGenerateToast && setPdfGenerateToast({ open: false, generating: false });
            dispatch(updateGlobalLoaderState(false));
            console.error('PDF generation error:', error);
        }
        console.log('PDF generation ended in:', new Date().getTime() - time, 'ms');
    }, []);

    useEffect(() => {
        if (open) {
            dispatch(updateGlobalLoaderState(true));
            // explicit wait for Portfolio Composition data dependent
            // on Runpipe and goal calculator api calls and subsequent
            // data modeling for highcharts (Doughnutchart component)
            const timerId = setTimeout(() => {
                generatePDF().catch(console.error);
            }, 500);
            // clear time out when component off loads DOM
            return () => {
                clearTimeout(timerId);
            };
        }
    }, [open, generatePDF]);

    if (!open) return null;

    return (
        <div>
            <Box sx={boxStyle}>
                <PDFWrapper>
                    <InnerWrapperNewCover
                        className="all-pdf"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        {children}
                    </InnerWrapperNewCover>
                </PDFWrapper>
            </Box>
        </div>
    );
};

export default PDFViewer;
